// Avatar ramp + name hash + name-keyed pair live in common/lib/avatar-colors so
// features/ can share them (features may not import modules/). Re-exported below
// so existing modules/ consumers keep importing from this barrel.
import { AVATAR_RAMP, nameHash, getAvatarColorPair } from '@/common/lib/avatar-colors';

/**
 * Conversation accent color palette - enterprise-grade neutrals only
 * Applied at the conversation level, not per-module
 */
export const HUB_ACCENT_COLORS = [
  { name: 'None', value: 'transparent' },     // No accent rail
  { name: 'Ink Blue', value: '#9FAED3' },     // Soft ink blue
  { name: 'Slate', value: '#AEB7C3' },        // Cool gray-blue
  { name: 'Olive', value: '#A9B9A2' },        // Soft olive green
  { name: 'Teal', value: '#9EBAB4' },         // Muted teal
  { name: 'Earth', value: '#B8AA9F' },        // Warm earth tone
];

/**
 * Label color palette - vibrant colors for task labels
 * Used for labeling tasks, cards, and other categorizable items
 */
export const LABEL_COLORS = [
  '#fca5a5', // soft red
  '#fecdd3', // soft rose
  '#f9a8d4', // soft pink
  '#e9d5ff', // soft lavender
  '#c4b5fd', // soft purple
  '#a5b4fc', // soft indigo
  '#93c5fd', // soft blue
  '#67e8f9', // soft cyan
  '#99f6e4', // soft teal
  '#86efac', // soft green
  '#a7f3d0', // soft mint
  '#fde047', // soft yellow
  '#fdba74', // soft orange
];

/**
 * Module highlight color palette - each entry has a pastel background
 * and a darker textColor for coloring the module name in the sidebar.
 */
export const MODULE_HIGHLIGHT_COLORS: Array<{ value: string | null; label: string; textColor?: string }> = [
  { value: null,       label: 'None' },
  { value: '#ffebee', label: 'Cherry',     textColor: '#c62828' },
  { value: '#fbe9e7', label: 'Coral',      textColor: '#bf360c' },
  { value: '#fff3e0', label: 'Peach',      textColor: '#e65100' },
  { value: '#fff8e1', label: 'Apricot',    textColor: '#f57f17' },
  { value: '#fffde7', label: 'Butter',     textColor: '#9e9d24' },
  { value: '#f9fbe7', label: 'Lime',       textColor: '#558b2f' },
  { value: '#f1f8e9', label: 'Pistachio',  textColor: '#33691e' },
  { value: '#e8f5e9', label: 'Mint',       textColor: '#2e7d32' },
  { value: '#e0f2f1', label: 'Seafoam',    textColor: '#00695c' },
  { value: '#e0f7fa', label: 'Aqua',       textColor: '#00838f' },
  { value: '#e1f5fe', label: 'Ice',        textColor: '#0277bd' },
  { value: '#e3f2fd', label: 'Sky',        textColor: '#1565c0' },
  { value: '#e8eaf6', label: 'Periwinkle', textColor: '#283593' },
  { value: '#f3e5f5', label: 'Lavender',   textColor: '#6a1b9a' },
];

/** Look up the text color for a given highlight color value */
export function getModuleTextColor(highlightColor: string | null): string | undefined {
  if (!highlightColor) return undefined;
  const entry = MODULE_HIGHLIGHT_COLORS.find(c => c.value === highlightColor);
  return entry?.textColor;
}

// Legacy aliases for backwards compatibility
export const ACCENT_COLORS = HUB_ACCENT_COLORS;
export const MUTED_COLORS = HUB_ACCENT_COLORS;

export const DEFAULT_HUB_ACCENT_COLOR = '#AEB7C3';  // Slate as default
export const DEFAULT_MODULE_COLOR = 'transparent';
export const DEFAULT_ACCENT_COLOR = DEFAULT_HUB_ACCENT_COLOR;
export const DEFAULT_COLUMN_COLOR = 'var(--color-surface)';
export const DEFAULT_LABEL_COLOR = '#fca5a5';  // soft red as default

/**
 * Avatar color ramp — 6 soft pastel bg/text pairs.
 * Cycling order ensures no two adjacent contacts share a color.
 */
// Re-export the shared avatar ramp + name-keyed pair (defined in
// common/lib/avatar-colors) so existing modules/ consumers keep working.
export { AVATAR_RAMP, getAvatarColorPair };

// Legacy flat array (background colors only) for callers that only need bg
export const AVATAR_COLORS = AVATAR_RAMP.map(c => c.bg);

/**
 * Get a deterministic avatar background color from a name string.
 */
export function getAvatarColor(name: string): string {
  if (!name) return AVATAR_RAMP[0].bg;
  return AVATAR_RAMP[nameHash(name) % AVATAR_RAMP.length].bg;
}

/**
 * Email-hashed avatar color — preferred for contacts so the same person
 * keeps the same color even if their display name varies (e.g. "Sam"
 * vs. "Sam Altman"). Per contacts spec §9.
 */
export function getAvatarColorPairByEmail(email?: unknown, fallbackName?: unknown): { bg: string; text: string } {
  // Callers pass `contact.email || contact.emails?.[0]`, and `emails[0]` is an
  // object ({ value }) — not a string — so coerce defensively rather than
  // assume a string (a non-string used to crash `.toLowerCase()`).
  const asString = (v: unknown): string =>
    typeof v === 'string'
      ? v
      : v && typeof v === 'object' && typeof (v as { value?: unknown }).value === 'string'
        ? (v as { value: string }).value
        : '';
  const key = (asString(email) || asString(fallbackName)).toLowerCase().trim();
  if (!key) return AVATAR_RAMP[0];
  return AVATAR_RAMP[nameHash(key) % AVATAR_RAMP.length];
}

/**
 * Deterministic bubble color for a note posted by another team member.
 * Uses the AVATAR_RAMP but permanently skips index 4 (amber, #FAEEDA): that
 * pale yellow is visually indistinguishable from the own-note bubble (#FEF3C7
 * in ContactEditDesktopTimeline), so assigning it to another author would make
 * their note read as one of yours. The two yellows are deliberately distinct
 * constants — this skip is the guard that keeps them from colliding on screen.
 */
const NOTE_AUTHOR_RAMP = AVATAR_RAMP.filter((_, i) => i !== 4);

export function getNoteAuthorColor(authorName: string): { bg: string; text: string } {
  if (!authorName) return NOTE_AUTHOR_RAMP[0];
  return NOTE_AUTHOR_RAMP[nameHash(authorName) % NOTE_AUTHOR_RAMP.length];
}

/**
 * Get initials for an avatar chip.
 * Fallback chain: First+Last → single initial → company first letter → null (show icon).
 */
export function getInitials(
  firstName?: string | null,
  lastName?: string | null,
  company?: string | null,
): string | null {
  const first = firstName?.trim();
  const last = lastName?.trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first[0].toUpperCase();
  if (last) return last[0].toUpperCase();
  if (company?.trim()) return company.trim()[0].toUpperCase();
  return null;
}
